/*
 * Size priority & sorting.
 *
 * For every "Size" variant selector on the page (dropdown or button picker),
 * this module:
 *   1. Sorts the dropdown options smallest -> largest (by width, then length).
 *   2. Pre-selects a default size once per selector:
 *        - the first IN-STOCK size from the configured priority list, else
 *        - the smallest in-stock size, else
 *        - the smallest size overall.
 *
 * Config is provided by the theme via window.Eurus.sizeDefault:
 *   { enabled: Boolean, optionName: String, priority: [String, ...] }
 *
 * The sort runs on every (re)render via a MutationObserver, so it survives the
 * theme's fetch-and-swap variant updates. The default is applied only once per
 * selector instance (tracked with a flag on the persistent component root) and
 * is skipped on product pages opened with an explicit ?variant= deep link.
 */
(function () {
  var hasWindow = typeof window !== 'undefined';
  if (hasWindow && window.__eurusSizePriorityLoaded) return;
  if (hasWindow) window.__eurusSizePriorityLoaded = true;

  function cfg() {
    return (hasWindow && window.Eurus && window.Eurus.sizeDefault) || null;
  }
  function isEnabled() {
    var c = cfg();
    return !!c && c.enabled !== false;
  }
  function optionName() {
    var c = cfg();
    return (c && c.optionName) || 'Size';
  }
  function priorities() {
    var c = cfg();
    var list = (c && c.priority) || [];
    return list.map(normSize).filter(Boolean);
  }

  // Normalize a size label for matching/parsing: lowercase, unify the
  // multiplication sign, strip foot/inch marks and quotes, drop whitespace.
  // "8' x 10'" / "8' × 10'" / "8 X 10" -> "8x10"
  function normSize(s) {
    return (s == null ? '' : String(s))
      .toLowerCase()
      .replace(/×/g, 'x') // × -> x
      .replace(/['"‘’“”′″]/g, '') // ' " ' ' " " ′ ″
      .replace(/\s+/g, '')
      .trim();
  }

  // Parse "W x L" into numeric feet. Returns null when not two numeric dims.
  function parseDims(value) {
    var parts = normSize(value).split('x');
    if (parts.length !== 2) return null;
    var w = parseFloat(parts[0]);
    var l = parseFloat(parts[1]);
    if (isNaN(w) || isNaN(l)) return null;
    return { w: w, l: l };
  }

  // Width ascending, then length ascending. Unparseable values sink to the end
  // (caller keeps original order among them for a stable result).
  function compareSizes(aVal, bVal) {
    var a = parseDims(aVal);
    var b = parseDims(bVal);
    if (a && b) return a.w !== b.w ? a.w - b.w : a.l - b.l;
    if (a) return -1;
    if (b) return 1;
    return 0;
  }

  function matchesSizeName(name) {
    if (!name) return false;
    var target = optionName().toLowerCase();
    var n = name.toLowerCase();
    // Dropdowns/radios use the raw option name ("Size"); combined-listing
    // radios suffix the position ("Size-1").
    return n === target || n.indexOf(target + '-') === 0;
  }

  // The component root that PERSISTS across the theme's inner innerHTML swaps,
  // so the "default applied" flag is not lost on a variant re-render.
  function stableRoot(el) {
    return (
      el.closest('[x-data*="xVariantSelect"]') ||
      el.closest('.variant-selects') ||
      el.closest('form') ||
      el.parentElement
    );
  }

  function isProductPageDeepLink() {
    if (typeof location === 'undefined') return false;
    return /\/products\//.test(location.pathname) && /[?&]variant=/.test(location.search);
  }

  // ---- sorting (dropdowns only — "the dropdown is sorted") ----
  function sortSelect(select) {
    var opts = Array.prototype.slice.call(select.options);
    if (opts.length < 2) return;
    var sorted = opts
      .map(function (el, i) {
        return { el: el, i: i };
      })
      .sort(function (A, B) {
        return compareSizes(A.el.value, B.el.value) || A.i - B.i;
      })
      .map(function (o) {
        return o.el;
      });

    var alreadySorted = opts.every(function (el, i) {
      return el === sorted[i];
    });
    if (alreadySorted) return;

    var selectedValue = select.value; // detaching options can drop selection
    var frag = document.createDocumentFragment();
    sorted.forEach(function (el) {
      frag.appendChild(el);
    });
    select.appendChild(frag);
    if (select.value !== selectedValue) select.value = selectedValue; // restore
  }

  // ---- default selection ----
  // items: [{ el, value, available }] in DOM order (already sorted for selects)
  function pickDesired(items) {
    if (!items.length) return null;
    var prios = priorities();
    for (var p = 0; p < prios.length; p++) {
      for (var i = 0; i < items.length; i++) {
        if (items[i].available && normSize(items[i].value) === prios[p]) return items[i];
      }
    }
    for (var j = 0; j < items.length; j++) {
      if (items[j].available) return items[j]; // smallest available
    }
    return items[0]; // nothing available -> smallest overall
  }

  function fireChange(el) {
    el.dispatchEvent(new Event('input', { bubbles: true }));
    el.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function applyDefaultToSelect(select) {
    var root = stableRoot(select);
    if (!root || root.dataset.sizeDefaulted === '1') return;
    root.dataset.sizeDefaulted = '1';
    if (isProductPageDeepLink()) return;

    var items = Array.prototype.slice.call(select.options).map(function (o) {
      return { el: o, value: o.value, available: o.dataset.available !== 'false' };
    });
    var chosen = pickDesired(items);
    if (!chosen || select.value === chosen.el.value) return;

    items.forEach(function (it) {
      it.el.removeAttribute('selected');
    });
    chosen.el.setAttribute('selected', 'selected');
    chosen.el.selected = true;
    select.value = chosen.el.value;
    fireChange(select);
  }

  function applyDefaultToRadios(group) {
    if (!group.length) return;
    var root = stableRoot(group[0]);
    if (!root || root.dataset.sizeDefaulted === '1') return;
    root.dataset.sizeDefaulted = '1';
    if (isProductPageDeepLink()) return;

    var items = group.map(function (r) {
      return {
        el: r,
        value: r.value,
        available: r.dataset.available !== 'false' && !r.classList.contains('disabled'),
      };
    });
    var chosen = pickDesired(items);
    if (!chosen || chosen.el.checked) return;

    chosen.el.checked = true;
    fireChange(chosen.el);
  }

  function processRadios(scope) {
    var radios = Array.prototype.slice
      .call(scope.querySelectorAll('input[type="radio"]'))
      .filter(function (r) {
        return matchesSizeName(r.name);
      });
    if (!radios.length) return;

    var processed = [];
    radios.forEach(function (r) {
      var container = r.closest('fieldset') || r.form || r.parentElement;
      var already = processed.some(function (g) {
        return g.container === container && g.name === r.name;
      });
      if (already) return;
      processed.push({ container: container, name: r.name });
      var group = radios.filter(function (x) {
        return (x.closest('fieldset') || x.form || x.parentElement) === container && x.name === r.name;
      });
      applyDefaultToRadios(group);
    });
  }

  function process(scope) {
    if (!isEnabled()) return;
    var root = scope && scope.querySelectorAll ? scope : document;
    Array.prototype.slice.call(root.querySelectorAll('select')).forEach(function (select) {
      if (!matchesSizeName(select.name)) return;
      sortSelect(select);
      applyDefaultToSelect(select);
    });
    // A scoped node may itself be a select (e.g. injected directly).
    if (root.matches && root.matches('select') && matchesSizeName(root.name)) {
      sortSelect(root);
      applyDefaultToSelect(root);
    }
    processRadios(root);
  }

  function runProcess() {
    try {
      process(document);
    } catch (e) {
      /* never let selector logic break the page */
    }
  }

  var scheduled = false;
  function schedule() {
    if (scheduled) return;
    scheduled = true;
    // setTimeout (not rAF) so the pass still runs in background/inactive tabs.
    setTimeout(function () {
      scheduled = false;
      runProcess();
    }, 0);
  }

  function init() {
    runProcess(); // immediate first pass (DOM is parsed for a deferred script)
    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          if (added[j].nodeType === 1) {
            schedule();
            return;
          }
        }
      }
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });
  }

  if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }
    document.addEventListener('alpine:initialized', schedule);
  }

  // Exposed for unit testing under Node; harmless in the browser.
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
      normSize: normSize,
      parseDims: parseDims,
      compareSizes: compareSizes,
      matchesSizeName: matchesSizeName,
      pickDesired: pickDesired,
      sortSelect: sortSelect,
      applyDefaultToSelect: applyDefaultToSelect,
      applyDefaultToRadios: applyDefaultToRadios,
      isProductPageDeepLink: isProductPageDeepLink,
      process: process,
    };
  }
})();
